import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, Space} from 'antd';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import {ExclamationCircleFilled, PlusOutlined} from '@ant-design/icons';
import PropTypes from 'prop-types';
import {StyledInvoiceTabsContainer, StyledInvoicesTabsContent} from './StyledInvoicesTabs';
import {StyledTableFiltersDivider} from '../../TransactionsPage/TableFilters/StyledTableFilters';
import UploadInvoicesModal from '../UploadInvoicesModal';
import MultipleSelectionBar from '../../../MultipleSelectionBar';
import Empty from '../../../Empty';
import UnmatchedTotals from '../tabComponents/UnmatchedTotals';
import InvoicesTab from '../tabs/InvoicesTab';
import ExportModal from '../../TransactionsPage/ExportModal';
import {DEFAULT_MODAL_PROPS, DEFAULT_PAGE_SIZE, invoicePaymentStatusConstants} from '../../../../constants';
import {invoicesActions} from '../../../../state/actions/invoice.actions';
import {
  elementHelpers,
  fileHelpers,
  invoicesHelpers,
  objectHelpers,
  systemHelpers,
  tabHelpers, tableHelpers
} from '../../../../utils/helpers';
import {firebaseEvents} from '../../../../snippets/firebase';
import {useHasAccess, useTableState} from '../../../../hooks';

const defaultQuery = {
  per_page: DEFAULT_PAGE_SIZE,
  sort_by: 'created_at',
  sort_order: 'desc'
}

const {logEvent} = systemHelpers;

const InvoicesTabs = ({
  handleUpdateTotal,
  unmatchedTotal,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [t] = useTranslation(['main', 'invoices']);
  const {
    tableData, setTableData,
    tableQuery, setTableQuery,
    loading, setLoading,
    selectedRowKeys, setSelectedRowKeys,
    finishTableLoading,
  } = useTableState({defaultLoading: false, defaultQuery});
  const [uploadInvoicesModalProps, setUploadInvoicesModalProps] = useState(DEFAULT_MODAL_PROPS);
  const [exportModalProps, setExportModalProps] = useState(DEFAULT_MODAL_PROPS);
  const [searchParams, setSearchParams] = useSearchParams();

  const dispatch = useDispatch();

  const hasCreateAccess = useHasAccess('POST_INVOICES_BATCH');

  const trans = (key) => t(`invoices:${key}`);

  const emptyT = (key) => trans(`empty.${key}`);

  const handleOnAddClick = () => setUploadInvoicesModalProps({...uploadInvoicesModalProps, open: true});

  const handleOnExportClick = () => setExportModalProps({...exportModalProps, open: true});

  const filtersLeftSideContent = (
    <UnmatchedTotals
      className='unmatched-totals'
      data={unmatchedTotal}
      loading={unmatchedTotal?.loading}
    />
  );

  const filtersRightSideContent = (
    <Space size={0}>
      <Button
        onClick={handleOnExportClick}
        type='default'
        size='large'
      >
        {t('export')}
      </Button>
      {hasCreateAccess && (
        <>
          <StyledTableFiltersDivider type='vertical' />
          <Button
            icon={<PlusOutlined />}
            onClick={handleOnAddClick}
            type='primary'
            size='large'
          >
            {t('invoice')}
          </Button>
        </>
      )}
    </Space>
  );

  useEffect(() => {
    const query = objectHelpers.getSearchParams(searchParams);

    setTableQuery(query);
    loadData({ initial: true, query });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = ({query = null, initial = false}) => {
    const viewQuery = tabHelpers.getViewQuery({initial, query, queries: tableQuery, single: true});
    setTableQuery(viewQuery);

    setLoading(true);

    dispatch(invoicesActions.getInvoices({
      query: viewQuery,
      successCallback: (response) => {
        const data = tabHelpers.getUpdatedTableData({
          tableData: tableData,
          response,
          initial,
          rowsKey: 'invoices'
        });
        setTableData(data);
        finishTableLoading();
      },
      errorCallback: finishTableLoading
    }));
  }

  const updateTotal = () => handleUpdateTotal && handleUpdateTotal();

  const handleOnPaginate = (view, activePage) =>
    tabHelpers.handleOnPaginate({view, activePage, tableData, setTableData, loadDataFunc: loadData, single: true})

  const handleOnFilter = (incomingQuery) => {
    let query = {
      ...objectHelpers.getSearchParams(searchParams),
      ...incomingQuery
    };

    // include PAID_OUTSIDE when filtering by PAID status
    if (objectHelpers.isValidValue(query.payment_statuses) && query.payment_statuses === invoicePaymentStatusConstants.PAID) {
      query.payment_statuses = [query.payment_statuses, invoicePaymentStatusConstants.PAID_OUTSIDE];
    }

    setSearchParams(objectHelpers.omitInvalidValues(query));
    loadData({ initial: true, query });
  }

  const handleOnTableChange = (pagination, filters, sorter, extra) => {
    const {action} = extra;
    if (action === 'sort') {
      const {order, field} = sorter;
      loadData({
        initial: true,
        query: field ? {
          sort_by: field,
          sort_order: order === 'descend' ? 'desc' : 'asc'
        } : defaultQuery
      });
    }
  }

  const emptyScreen = (
    <Empty description={emptyT('description')} title={emptyT('title')}>
      <Button
        onClick={handleOnAddClick}
        size='large'
        type='primary'
      >
        {trans('uploadInvoice')}
      </Button>
    </Empty>
  );

  const tabProps = {
    data: tableData?.rows || [],
    empty: emptyScreen,
    tableFiltersProps: {
      defaultQuery: tableQuery,
      leftSideContent: filtersLeftSideContent,
      rightSideContent: filtersRightSideContent
    },
    loading,
    onFilter: handleOnFilter,
    tableProps: {
      onChange: handleOnTableChange,
      onRow: (record, index) => ({
        onClick: (e) => handleOnRowClick(e, record, index)
      }),
      pagination: tabHelpers.getTablePagination({handleOnPaginate, tableData, single: true}),
      rowSelection: { selectedRowKeys, onChange: setSelectedRowKeys }
    }
  }

  const handleCancelUploadInvoicesModal = () => setUploadInvoicesModalProps({...uploadInvoicesModalProps, ...DEFAULT_MODAL_PROPS});

  const handleOkUploadInvoicesModal = (invoices, successCallback, errorCallback) => {
    setUploadInvoicesModalProps({...uploadInvoicesModalProps, loading: true});
    dispatch(invoicesActions.batchCreateInvoices(
      {invoices},
      () => {
        loadData({initial: true});
        updateTotal();
        handleCancelUploadInvoicesModal();
        logEvent(firebaseEvents.INVOICES_ADD_NEW);
      },
      (resp) => {
        errorCallback && errorCallback(resp);
        setUploadInvoicesModalProps({...uploadInvoicesModalProps, loading: false});
      }
    ));
  }

  const handleCancelSelected = () => setSelectedRowKeys([]);

  const handleDeleteSelected = () => {
    elementHelpers.showConfirmModal({
      content: trans('modal.deleteInvoices.description'),
      icon: <ExclamationCircleFilled />,
      okType: 'danger',
      onOk: () => {
        if (selectedRowKeys.length) {
          setLoading(true);
          dispatch(invoicesActions.batchDeleteInvoices(
            selectedRowKeys,
            () => {
              handleCancelSelected();
              loadData({initial: true});
              updateTotal();
              logEvent(firebaseEvents.INVOICES_DELETE_INVOICE);
            },
            () => setLoading(false)
          ));
        } else {
          handleCancelSelected();
        }
      },
      title: trans('modal.deleteInvoices.title'),
      translate: t
    });
  }

  const handleOnRowClick = (e, record) => {
    const url = location.pathname + location.hash + location.search;

    dispatch(invoicesActions.saveInvoicesListUrl(url, tableQuery));
    navigate(invoicesHelpers.getInvoiceDetailsLink(record.id));
  }

  const handleCancelExportModal = () => setExportModalProps({...exportModalProps, open: false, loading: false, error: null});

  const handleOkExport = (data) => {
    const query = tableHelpers.formatFilterQuery({
      data,
      dateKeys: { gte: 'date_gte', lte: 'date_lte'}
    });
    if (!query) return;

    setExportModalProps({...exportModalProps, loading: true});

    const fileFormat = data?.format;
    const action = fileFormat === 'csv'
      ? invoicesActions.downloadCsvInvoices
      : invoicesActions.downloadZipInvoices;

    dispatch(action({
      query,
      successCallback: (response) => {
        if (fileFormat === 'csv') {
          fileHelpers.handleCSVExport(
            response,
            tableHelpers.getExportFileName({baseName: 'invoices', data})
          );
        } else {
          window.open(response.url, 'download');
        }

        handleCancelExportModal();
        logEvent(firebaseEvents.INVOICES_EXPORT);
      },
      errorCallback: () => setExportModalProps({
        ...exportModalProps,
        open: true,
        loading: false,
        error: t('fileIsTooLargeToDownloadMessage')
      })
    }));
  }

  return (
    <>
      <StyledInvoiceTabsContainer>
        <StyledInvoicesTabsContent>
          <MultipleSelectionBar
            actions={[
              {onClick: handleDeleteSelected, label: t('delete')},
            ]}
            onCancel={handleCancelSelected}
            selectedRowKeys={selectedRowKeys}
          />
          <InvoicesTab {...tabProps} />
        </StyledInvoicesTabsContent>
      </StyledInvoiceTabsContainer>

      <UploadInvoicesModal
        {...uploadInvoicesModalProps}
        onCancel={handleCancelUploadInvoicesModal}
        onOk={handleOkUploadInvoicesModal}
      />

      <ExportModal
        {...exportModalProps}
        options={['csv', 'zip']}
        handleCancel={handleCancelExportModal}
        handleOk={handleOkExport}
        title={`${t('export')} ${t('invoices')}`}
      />

    </>
  );
}

InvoicesTabs.propTypes = {
  handleUpdateTotal: PropTypes.func,
  unmatchedTotal: PropTypes.shape({
    due: PropTypes.number,
    loading: PropTypes.bool,
    overdue: PropTypes.number,
    upcoming: PropTypes.number,
  }),
}

export default InvoicesTabs;
